/* eslint-disable max-len */
import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { NonJobEventPreviewOptions } from '../create-non-job-event/non-job-event.types';
import { ZuperDialogService } from '@zuper/components/zuper-dialog';
import { ZuperDrawerService } from '@zuper/components/zuper-drawer';
import { DataCollectorService } from 'app/core/services/data_collector_services/data_collector_service.service';
import { LocalisationService } from 'app/core/services/localisation/zuper.localisation.service';
import { UtilityService } from 'app/core/services/utility_functions/utility_functions.service';
import { DispatchBoardService } from '../../../../projects/dispatch-board/src/app/components/dispatch-board/dispatch-board.service';
import _ from 'lodash';
import { ActionSelectorComponent } from '../action-selector/action-selector.component';
import { ActionSelectorOption } from '../action-selector/action-selector.interface';
import moment from 'moment';

@Component({
  selector: 'non-job-event-details',
  templateUrl: './non-job-event-details.component.html',
  styleUrls: ['./non-job-event-details.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class NonJobEventDetailsComponent implements OnInit, OnChanges {

  @Input() nonJobEventUid: string;
  @Input() openDrawer: boolean;
  @Input() options: NonJobEventPreviewOptions;
  @Input() timezone: string;
  @Input() drawerName: string = 'NonJobEventDetailsDrawer';
  @Output() actionEmitter: EventEmitter<any> = new EventEmitter();

  nonJobEventDetails: any;
  timezoneCode: string;
  showUserTeamAndRole: boolean = true;
  isDeleted: boolean = false;
  isLoading: boolean = false;
  timezoneDisplayName: string;
  accessControl:  { view: boolean; edit: boolean; delete: boolean };

  constructor(
    private zuperDrawerService: ZuperDrawerService,
    private zuperDialogService: ZuperDialogService,
    private localisationService: LocalisationService,
    private dataCollectorService: DataCollectorService,
    private utilityService: UtilityService,
    private dispatchBoardService: DispatchBoardService  ) { }

  ngOnInit(): void {
    this.localisationService.setDefaultLanguage();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('openDrawer' in changes) {
      this.openDrawer = changes?.openDrawer?.currentValue;
      const drawerRef = this.zuperDrawerService.getComponent(this.drawerName);
      if (drawerRef) {
        if (this.openDrawer) {
          drawerRef.open();
        } else {
          drawerRef.close();
        }
      }
    }
  }

  //Drawer controls
  drawerOpenedChanged(isOpened: any): void {
    if (isOpened) {
      if (!this.timezone) {
        this.timezone = this.utilityService.timeZone;
      }
      this.timezoneCode = 'GMT ' + moment().tz(this.timezone).format('Z');
      this.timezoneDisplayName = this.utilityService.getTimezoneDisplayName(this.timezone);
      this.getNonJobEventDetails();
    }
  }

  emitAction(event: any): void {
    this.actionEmitter.emit(event);
  }

  closeDrawer(event?: any): void {
    this.openDrawer = false;
    const drawerRef = this.zuperDrawerService.getComponent(this.drawerName);
    if (drawerRef) {
      drawerRef.close();
      this.emitAction({ isClosed: true, ...event });
    }
  }

  formatNonJobEventDetails(nonJobEventDetails: any): any {
    if (nonJobEventDetails) {
      let nonJobEventDetailsObj = _.cloneDeep([nonJobEventDetails]);
      nonJobEventDetailsObj = nonJobEventDetailsObj?.map((eventDetails: any) => ({
        ...eventDetails,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        assigned_to: eventDetails?.assigned_to.map((assignee: any) => ({ ...assignee, hasValidProfilePicture: this.utilityService.hasValidProfilePicture(assignee?.user?.profile_picture) })),
        startEndDateTime: this.dispatchBoardService.getStartEndTime(nonJobEventDetails?.start_time, nonJobEventDetails?.end_time, false, null, false, false, this.timezone),
        timezoneBasedStartEndDateTime: eventDetails?.timezone ? this.dispatchBoardService.getStartEndTime(nonJobEventDetails?.start_time, nonJobEventDetails?.end_time, false, null, false, false, eventDetails?.timezone): ['', ''],
        timezoneCode: eventDetails?.timezone ? `GMT ${moment().tz( eventDetails?.timezone).format('Z')}`: '',
        timezoneDisplayName: this.utilityService.getTimezoneDisplayName(eventDetails?.timezone ? eventDetails?.timezone : this.utilityService.timeZone),
        eventAddress: this.dispatchBoardService.getAddressText(nonJobEventDetails?.event_address),
        categoryColor: eventDetails?.event_category?.category_color || '#e67e22',
        recurrenceText: eventDetails?.is_recurrence && eventDetails?.recurrence?.rrule ? this.utilityService.getRruleText(eventDetails?.recurrence?.rrule, this.timezone): ''
      }));
      return nonJobEventDetailsObj[0];
    }
    else {
      return nonJobEventDetails;
    }
  }

  getNonJobEventDetails(): void {
    if (this.nonJobEventUid) {
      this.isLoading = true;
      const nonJobEventUrl: string = `${this.dataCollectorService.baseurl()}/non_job_events/${this.nonJobEventUid}`;
      this.dataCollectorService.getData(nonJobEventUrl).subscribe((response: any) => {
        this.nonJobEventDetails = this.formatNonJobEventDetails(response?.data);
        this.accessControl = this.dispatchBoardService.checkNonJobEventAccess(this.nonJobEventDetails);
        this.isDeleted = false;
        this.isLoading = false;
      },
        (error: any) => {
          this.isLoading = false;
          this.isDeleted = true;
        });
    }
  }

  deleteNonJobEvent(): void {
    if (this.nonJobEventUid && this.nonJobEventDetails) {
      let deleteParam: any = {};
      if (this.nonJobEventDetails?.is_recurrence) {
        const actionSelectorOption: ActionSelectorOption = {
          title: `${this.utilityService.getLocaleValue('non_job_event.recurrence.delete.title', 'Delete Recurring Non Job Event')}`,
          options: [
            {
              key: 'SINGLE_EVENT',
              optionTitle: this.utilityService.getLocaleValue('non_job_event.recurrence.delete.event_only_title', 'Delete this event only'),
              optionMessage: `${this.utilityService.getLocaleValue('non_job_event.recurrence.event_only_message', 'Applies to this event only')} (${this.nonJobEventDetails?.startEndDateTime[0]})`
            },
            {
              key: 'ALL_FUTURE_EVENTS',
              optionTitle: this.utilityService.getLocaleValue('non_job_event.recurrence.delete.upcoming_event_title', 'Delete this and upcoming events'),
              optionMessage: `${this.utilityService.getLocaleValue('non_job_event.recurrence.upcoming_event_message', 'Applies to this and upcoming events in the series.')}`
            }
          ],
          primaryActionButtonText: this.utilityService.getLocaleValue('delete.text', 'Delete'),
          primaryActionButtonType: 'error'
        };
        const actionSelectorDialogRef = this.zuperDialogService.openCustom(ActionSelectorComponent, { data: actionSelectorOption, extraClass: 'non-job-delete-recurrence-dialog' });
        actionSelectorDialogRef.afterClosed().subscribe((response: any) => {
          if (response?.isSelected) {
            if (response?.selectedType) {
              deleteParam['delete_recurrence_type'] = response?.selectedType;
            }
            else {
              deleteParam = {};
            }
            this.dispatchBoardService.deleteNonJobEvent(this.nonJobEventUid, deleteParam, (deleteStatus: boolean) => {
              if (deleteStatus) {
                this.dispatchBoardService.trackMixPanelEvent('non_job_event_deleted');
                this.closeDrawer({ action: 'delete', isRecurrence: deleteParam['delete_recurrence_type'] === 'ALL_FUTURE_EVENTS', eventUid: this.nonJobEventUid });
              }
            });
          }
        });
      } else {
        const deleteDialogRef = this.dispatchBoardService.nonJobEventDetailsChangeConfirmationDialog(this.nonJobEventDetails, 'delete');
        deleteDialogRef.afterClosed().subscribe((status: boolean) => {
          if (status) {
            this.dispatchBoardService.deleteNonJobEvent(this.nonJobEventUid, deleteParam, (deleteStatus: boolean) => {
              if (deleteStatus) {
                this.dispatchBoardService.trackMixPanelEvent('non_job_event_deleted');
                this.closeDrawer({ action: 'delete', isRecurrence: false, eventUid: this.nonJobEventUid });
              }
            });
          }
        });
      }
    }
  }

  editNonJobEvent(): void {
    if (this.nonJobEventUid && this.nonJobEventDetails) {
      this.emitAction({ isClosed: false, eventDetails: this.nonJobEventDetails, action: 'edit' });
    }
  }
}
